import type { CartesianScaleOptions, ChartData, ChartDataset, ChartOptions, ChartType, ChartTypeRegistry, DefaultDataPoint, ScaleOptionsByType, TimeScaleOptions } from 'chart.js'
import type { DeepPartial } from '@/types/index.types'
import type { Color, ColorOrPattern } from '@/utils/colors'

export enum DimensionTypes {
  AUTO = 'auto',
  LOCATION = 'location',
  PROVIDER = 'provider',
  VEHICLETYPE = 'vehicleType',
  DATE = 'date',
  UNKNOWN = 'unknown',
}
export type DimensionType = `${DimensionTypes}`

export interface Status {
  code: string
  since: string
  detail: string
}

export interface DayStatus {
  day: string
  status: Status[]
}

interface SerieBase {
  name?: string
  label?: string
  category?: string
  color?: ColorOrPattern | ColorOrPattern[]
  status?: DayStatus[]
  unlocked?: boolean
  dataIndex?: number
}

export interface SerieDataObject { x: string | number, y: number }
export type SerieDataBase = (number | [number, number])
export type SerieDataEstimated = [number, number, boolean]
export type SerieData = SerieDataObject | SerieDataBase | SerieDataEstimated

export interface Serie extends SerieBase {
  data: SerieData[]
}

export interface SeriePercentValue extends SerieBase {
  percent: number
  value: number
  estimated?: boolean
}

export type SeriePercent = SeriePercentValue | SeriePercentOnly

export interface SeriePercentOnly extends SerieBase {
  percent: number
  estimated?: boolean
}

export interface NotEstimatedSerie extends Serie {
  data: SerieDataBase[]
}

type Estimated = {
  estimated: boolean
} | {
  data: SerieDataEstimated[]
}

export type EstimatedSerie = Serie & Estimated
export type MaybeEstimatedSerie = Serie | EstimatedSerie

export type AnySerie = MaybeEstimatedSerie | SeriePercent

export enum TimeModes {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}
export type TimeMode = `${TimeModes}`

export interface MetricDataSeries {
  labels?: string[]
  categories?: string[]
  series: MaybeEstimatedSerie[]
}

export type StatusScales = DeepPartial<ChartOptions<'bar'>['scales']> & {
  yStatus: DeepPartial<CartesianScaleOptions>
  xStatus: DeepPartial<TimeScaleOptions>
}

export type WeekendScales = DeepPartial<ChartOptions<'bar'>['scales']> & {
  yWeekend: DeepPartial<CartesianScaleOptions>
  xWeekend: DeepPartial<TimeScaleOptions>
}

export interface BaseScales<T extends ChartType> {
  x: DeepPartial<ScaleOptionsByType<ChartTypeRegistry[T]['scales']>>
  y: DeepPartial<ScaleOptionsByType<ChartTypeRegistry[T]['scales']>>
}

export type MetricScales<T extends ChartType> = BaseScales<T> & WeekendScales | BaseScales<T> & StatusScales | BaseScales<T> & StatusScales & WeekendScales
export type MetricChartDataset<T extends ChartType = 'bar', D = DefaultDataPoint<T>> = DeepPartial<ChartDataset<T, D> & AnySerie>
export type MetricChartData<T extends ChartType = 'bar'> = DeepPartial<ChartData<T>> & { datasets: MetricChartDataset<T>[] }

export type MetricOptions<T extends ChartType = 'bar'> = ChartOptions<T> & {
  dimension?: DimensionType
  label?: (d: any, dataIndex?: number) => string | string
  color?: (d: any, dataIndex?: number) => Color | Color
  colors?: Palette
  scales?: MetricScales<T>
  timeMode?: TimeMode
  isCompare?: boolean
  weather?: boolean
  weekend?: boolean
}

export interface MetricChart<T extends ChartType = 'bar', TO extends MetricOptions<T> = MetricOptions<T>> {
  data: MetricDataSeries
  type?: 'category' | 'time'
  options?: DeepPartial<TO>
  legend?: boolean
}

export interface MetricPercentChart<T extends ChartType = 'bar', TO extends MetricOptions<T> = MetricOptions<T>> {
  data: SeriePercentValue[]
  options?: DeepPartial<TO>
  legend?: boolean
}

export type BarMetricOptions<T extends ChartType = 'bar'> = MetricOptions<T> & {
  unstacked?: boolean
}

export type LineMetricOptions<T extends ChartType = 'line'> = MetricOptions<T> & {
  elements?: any
}

export type PieMetricOptions<T extends ChartType = 'pie'> = MetricOptions<T> & {
  elements?: any
}

export interface ChartTimeUnit {
  unit: string
  stepSize?: number
  displayFormats?: any
}
